'use client';

import { useUser } from '@auth0/nextjs-auth0';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTransition } from 'react';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { toggleSavePlan } from 'slices/cartSlice';
import { selectIsPlanSaved } from 'slices/selectors';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';
import { MedicareApiPlan } from 'types/api';
import { useSelectedPlanPersistence } from 'hooks/useSelectedPlanPersistence';
import { writeSessionItem } from 'lib/safeSessionStorage';

export const useSaveToggle = (plan: MedicareApiPlan) => {
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isSaved = useAppSelector((state) => selectIsPlanSaved(state, plan));
  const { persistPlan, removePlan } = useSelectedPlanPersistence();
  const [isPending, startTransition] = useTransition();

  const toggle = () => {
    if (!user) {
      writeSessionItem('pendingSavePlan', JSON.stringify(plan));
      const returnTo = searchParams.toString()
        ? `${pathname}?${searchParams.toString()}`
        : pathname;
      router.push(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
      return;
    }

    startTransition(async () => {
      if (!isSaved) {
        capture(ANALYTICS_EVENTS.planSaved);
        await persistPlan(plan, 'saved');
      } else {
        await removePlan(plan.id, 'saved', plan.planYear);
      }

      dispatch(toggleSavePlan(plan));
    });
  };

  return { toggle, isPending };
};
